import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, forkJoin } from 'rxjs';
import { ScormPackageModel, ScormPackageLocaleModel } from 'src/models/scormPackageModel';
import { ScormService } from 'src/services/scorm.service';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { ParcoursFormationService } from 'src/services/parcours-formation.service';
import { ParcoursFormationUtilisateurModel, PackageScormUtilisateurParcoursFormationModel } from 'src/models/parcoursFormationUtilisateurModel';
import { ParcoursFormationCardComponent } from './parcours-formation-card/parcours-formation-card.component';
import { MatDialog } from '@angular/material/dialog';
import { ScormDescriptionDialogComponent, ScormDescriptionDialogData } from './scorm-description-dialog/scorm-description-dialog.component';
import { GroupeFormationModel } from 'src/models/groupe-formation/groupe-formation-model';
import { GroupeFormationService } from 'src/services/groupe-formation.service';
import { GroupeFormationCardComponent } from './groupe-formation-card/groupe-formation-card.component';
import { CategorieService } from 'src/services/categorie.service';
import { CategorieModel, CategorieLocaleModel } from 'src/models/categorieModel';
import { ScormPackageProgressionModel } from 'src/models/scormPackageProgressionModel';
import { EProgressionPaquet } from 'src/enum/eProgressionPaquet';
import { MatIconModule } from '@angular/material/icon';
import { ScormPackageCardComponent } from './scorm-package-card/scorm-package-card.component';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ContenuSessionService } from 'src/services/contenu.session.service';
import { ContenuSessionModel } from 'src/models/contenuSessionModel';
import { ServiceNovoStudioModel } from 'src/models/pilotage/serviceNovoStudioModel';

const EN_COURS_CATEGORY_ID = -1;
const PRIORITE_CATEGORY_ID = -2;

@Component({
    selector: 'app-scorms-list',
    templateUrl: './scorms-list.component.html',
    styleUrls: ['./scorms-list.component.scss'],
    imports: [
        RouterModule,
        CommonModule,
        TranslateModule,
        ParcoursFormationCardComponent,
        GroupeFormationCardComponent,
        MatIconModule,
        ScormPackageCardComponent,
        FormsModule,
        MatProgressSpinnerModule
    ]
})
export class ScormsListComponent implements OnInit, OnDestroy {
  scormPackages: ScormPackageProgressionModel[] = [];
  parcoursFormations: ParcoursFormationUtilisateurModel[] = [];
  formationsGroupes: GroupeFormationModel[] = [];
  categories: CategorieModel[] = [];
  EProgressionPaquet = EProgressionPaquet;
  readonly EN_COURS_CATEGORY_ID = EN_COURS_CATEGORY_ID;
  readonly PRIORITE_CATEGORY_ID = PRIORITE_CATEGORY_ID;

  showCompletedCollapse = false;
  filteredCategoryId: number | null = null;
  searchTerm: string = '';
  isLoading: boolean = true;

  completedContentCount: number = 0;
  readonly DEMO_SCORM_LIMIT = 5;
  demoContentCountLoaded: boolean = false;
  isLoadingServices: boolean = false;
  private entiteChangeSubscription?: Subscription;

  constructor(
    private scormService: ScormService,
    private formationGroupeService: GroupeFormationService,
    private dialog: MatDialog,
    public translate: TranslateService,
    private utilisateurService: UtilisateurService,
    private parcoursFormationService: ParcoursFormationService,
    private categorieService: CategorieService,
    private contenuSessionService: ContenuSessionService,
    private snackBar: MatSnackBar
  ) {
    translate.setDefaultLang('fr');
  }
  ngOnInit(): void {
    this.chargerServicesEtContenu();

    this.entiteChangeSubscription = this.utilisateurService.entiteSelectionChange.subscribe(() => {
      this.chargerServicesEtContenu();
    });
  }

  ngOnDestroy(): void {
    this.entiteChangeSubscription?.unsubscribe();
  }

  private chargerServicesEtContenu(): void {
    const entiteSelectionnee = this.utilisateurService.entiteUtilisateurSelectionnee;
    if (!entiteSelectionnee) return;
    this.isLoadingServices = true;
    this.isLoading = true;


    this.utilisateurService.obtenirServicesEntiteEtAccesDiffusion(entiteSelectionnee.novoEntiteId).subscribe({
      next: (services: ServiceNovoStudioModel) => {
        entiteSelectionnee.estDemo = services.estDemo ?? false;
        entiteSelectionnee.dateDebutDemo = services.dateDebutDemo ?? null;
        entiteSelectionnee.dateFinDemo = services.dateFinDemo ?? null;
        entiteSelectionnee.estServiceStudio = services.estServiceStudio ?? false;
        entiteSelectionnee.estServiceStudioContenusPerso = services.estServiceStudioContenusPerso ?? false;
        entiteSelectionnee.aAccesDiffusion = services.aAccesDiffusion ?? false;
        this.isLoadingServices = false;
        this.loadContentForEntite(entiteSelectionnee.entiteId, entiteSelectionnee.utilisateurId);
      },
      error: (err) => {
        this.isLoadingServices = false;
        this.loadContentForEntite(entiteSelectionnee.entiteId, entiteSelectionnee.utilisateurId);
      }
    });
  }

  private loadContentForEntite(entiteId: number, utilisateurId: number): void {
    const totalToLoad = this.isDemoActive ? 4 : 3;
    const onLoaded = this.createLoadingTracker(totalToLoad, () => {
      this.isLoading = false;
      if (this.hasPriorityContent) {
        this.filteredCategoryId = this.PRIORITE_CATEGORY_ID;
      } else {
        this.filteredCategoryId = null;
      }
    });

    forkJoin({
      packages: this.scormService.obtenirTousLesPackagesAvecProgression(entiteId),
      formationsGroupes: this.formationGroupeService.ObtenirGroupeFormations()
    }).subscribe(({ packages, formationsGroupes }) => {
      this.scormPackages = packages;
      this.scormService.scormPackages = packages;

      this.formationsGroupes = formationsGroupes.filter(fg => fg.dateCompletee == null);
      formationsGroupes.forEach(fg => {
        fg.package = packages.find(p => p.scormPackageId === fg.packageId)!;
      });
      onLoaded();
    });

    this.parcoursFormationService.obtenirParcoursFormationsPourUnUtilisateurId(
      this.translate.currentLang
    ).subscribe(parcours => {
      this.parcoursFormations = parcours;
      onLoaded();
    });

    this.categorieService.obtenirCategories().subscribe(categories => {
      this.categories = categories;
      onLoaded();
    });

    if (this.isDemoActive) {
      this.checkDemoCompletedContentCount(entiteId, utilisateurId, onLoaded);
    }
  }

  openDescriptionDialog(scormPackage: ScormPackageModel, formationGroupeId?: number): void {
    const scormPackageProgression = this.scormPackages.find(p => p.scormPackageId === scormPackage.scormPackageId);

    if (!scormPackageProgression) {
      this.snackBar.open(
        this.translate.instant('SCORMS_LIST.CONTENU_INDISPONIBLE'),
        this.translate.instant('COMMUN.CLOSE'),
        { duration: 5000 }
      );

      return;
    }

    const dialogData: ScormDescriptionDialogData = {
      scormPackage: scormPackageProgression,
      estDemo: this.isDemoActive,
      formationGroupeId: formationGroupeId ?? null
    };
    const isMobile = window.innerWidth <= 640;
    const dialogConfig = isMobile
      ? {
        width: '100vw',
        height: '100vh',
        disableClose: false,
        panelClass: 'dialog-right-full-height',
        data: dialogData
      }
      : {
        width: '33.33vw',
        height: '100vh',
        disableClose: false,
        position: { right: '0' },
        panelClass: 'dialog-right-full-height',
        data: dialogData
      };
    const dialogRef = this.dialog.open(ScormDescriptionDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {

      }
    });
  }

  getLocalizedContent(locales: ScormPackageLocaleModel[]): ScormPackageLocaleModel {
    const currentLang = this.translate.currentLang || 'fr';
    return locales.find(locale => locale.langue.toLowerCase() === currentLang) || locales[0];
  }

  getLocalizedCategoryName(locales: CategorieLocaleModel[]): string {
    const currentLang = this.translate.currentLang || 'fr';
    return locales.find(locale => locale.langue.toLowerCase() === currentLang)?.nom || locales[0]?.nom || '';
  }

  getNextPackage(packages: PackageScormUtilisateurParcoursFormationModel[]): PackageScormUtilisateurParcoursFormationModel | undefined {
    return packages.find(p => !p.estComplete);
  }

  getLocalizedNom(parcours: ParcoursFormationUtilisateurModel): string {
    const currentLang = this.translate.currentLang || 'fr';
    return (parcours as any).locales[currentLang] ||
      (parcours as any).locales['fr'] ||
      (parcours as any).locales['en'] ||
      (parcours as any).locales['es'] ||
      '';
  }

  calculateProgress(parcours: ParcoursFormationUtilisateurModel): number {
    if (!parcours || !parcours.packages || parcours.packages.length === 0) {
      return 0;
    }
    const completedPackages = parcours.packages.filter(p => p.estComplete).length;
    const totalPackages = parcours.packages.length;
    return Math.round((completedPackages / totalPackages) * 100);
  }

  getCompletedCount(parcours: ParcoursFormationUtilisateurModel): number {
    return parcours.packages.filter(p => p.estComplete).length;
  }

  getTotalCount(parcours: ParcoursFormationUtilisateurModel): number {
    return parcours.packages.length;
  }

  getCompletedDuration(parcours: ParcoursFormationUtilisateurModel): number {
    return parcours.packages
      .filter(p => p.estComplete)
      .reduce((total, pkg) => total + this.getLocalizedContent(pkg.scormPackage.locales).dureeEnMinutes, 0);
  }

  calculateDurationProgress(parcours: ParcoursFormationUtilisateurModel): number {
    if (!parcours || !parcours.packages || parcours.packages.length === 0) {
      return 0;
    }
    const completedDuration = this.getCompletedDuration(parcours);
    return Math.round((completedDuration / parcours.dureeTotaleMinutes) * 100);
  }

  showDescription(scormPackage: ScormPackageModel, formationGroupeId?: number): void {
    this.openDescriptionDialog(scormPackage, formationGroupeId);
  }

  get nonCompletedParcours(): ParcoursFormationUtilisateurModel[] {
    return this.parcoursFormations.filter(
      pf => pf.packages.some(pkg => !pkg.estComplete) && this.estParcoursVisible(pf)
    );
  }

  get completedParcours(): ParcoursFormationUtilisateurModel[] {
    return this.parcoursFormations.filter(
      pf => pf.packages.length > 0 && pf.packages.every(pkg => pkg.estComplete) && this.estParcoursVisible(pf)
    );
  }

  toggleCompletedCollapse() {
    this.showCompletedCollapse = !this.showCompletedCollapse;
  }

  onCategoryToggle(categoryId: number | null): void {
    if (this.filteredCategoryId === categoryId) {
      this.filteredCategoryId = null;
    } else {
      this.filteredCategoryId = categoryId;
    }
  }

  get inProgressScormPackages(): ScormPackageProgressionModel[] {
    const filtered = this.scormPackages.filter(pkg => pkg.progression === EProgressionPaquet.EnCours);
    return this.filtrerSelonServices(filtered);
  }

  get displayedCategories(): CategorieModel[] {
    const filteredPackages = this.filtrerSelonServices(this.scormPackages);
    const categoryIdsInPackages = new Set(filteredPackages.flatMap(pkg => pkg.categorieIds));
    return this.categories.filter(cat => categoryIdsInPackages.has(cat.categorieId));
  }

  get filteredScormPackages(): ScormPackageProgressionModel[] {
    let packages: ScormPackageProgressionModel[];
    if (!this.filteredCategoryId) {
      packages = this.filtrerSelonServices(this.scormPackages);
    } else {
      packages = this.getPackagesByCategory(this.filteredCategoryId);
    }
    if (this.filteredCategoryId === PRIORITE_CATEGORY_ID || !this.searchTerm.trim()) {
      return packages;
    }
    const term = this.searchTerm.trim().toLowerCase();
    return packages.filter(pkg => {
      const localized = this.getLocalizedContent(pkg.locales);
      return localized.titre && localized.titre.toLowerCase().includes(term);
    });
  }

  get isDemoExpired(): boolean {
    const entite = this.utilisateurService.entiteUtilisateurSelectionnee;
    const result = entite?.estDemo && entite?.dateFinDemo && new Date(entite.dateFinDemo) < new Date();
    return result;
  }

  get isDemoActive(): boolean {
    const entite = this.utilisateurService.entiteUtilisateurSelectionnee;
    const result = entite?.estDemo && entite?.dateFinDemo && new Date(entite.dateFinDemo) > new Date();
    return result;
  }

  get shouldShowDemoExpiry(): boolean {
    return this.isDemoActive && this.demoContentCountLoaded && !this.isDemoLimitReached;
  }

  get dateFinDemo(): Date | null {
    const entite = this.utilisateurService.entiteUtilisateurSelectionnee;
    return entite?.dateFinDemo ? new Date(entite.dateFinDemo) : null;
  }

  get shouldShowNovoStudioWarning(): boolean {
    const entite = this.utilisateurService.entiteUtilisateurSelectionnee;
    const result = entite && !entite.estServiceStudio && !entite.estServiceStudioContenusPerso && !this.isLoadingServices;
    return result;
  }

  get shouldShowWarningOnly(): boolean {
    const result = this.isDemoExpired || this.shouldShowNovoStudioWarning;
    return result;
  }

  get isDemoLimitReached(): boolean {
    const entite = this.utilisateurService.entiteUtilisateurSelectionnee;
    const result = entite?.estDemo && this.demoContentCountLoaded && this.completedContentCount >= this.DEMO_SCORM_LIMIT;
    return result;
  }

  getPackagesByCategory(categoryId: number): ScormPackageProgressionModel[] {
    if (categoryId === EN_COURS_CATEGORY_ID) {
      return this.inProgressScormPackages;
    }
    const filtered = this.scormPackages.filter(pkg => pkg.categorieIds.includes(categoryId));
    return this.filtrerSelonServices(filtered);
  }

  filterPackagesByCategoryWithSearch(categoryId: number): ScormPackageProgressionModel[] {
    let packages = this.getPackagesByCategory(categoryId);
    if (this.filteredCategoryId === this.PRIORITE_CATEGORY_ID || !this.searchTerm.trim()) {
      return packages;
    }
    const term = this.searchTerm.trim().toLowerCase();
    return packages.filter(pkg => {
      const localized = this.getLocalizedContent(pkg.locales);
      return localized.titre && localized.titre.toLowerCase().includes(term);
    });
  }

  isParticipantTrainingGroup(formationGroupe: GroupeFormationModel): boolean {
    return formationGroupe.utilisateurs.some(p => p.utilisateurId === this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId && p.estResponsableFormation === false);
  }

  isSupervisorTrainingGroup(formationGroupe: GroupeFormationModel): boolean {
    return formationGroupe.utilisateurs.some(p => (p.utilisateurId === this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId && p.estResponsableFormation === true));
  }

  shouldShowProgressionIcon(pkg: ScormPackageProgressionModel): boolean {
    return pkg.progression === EProgressionPaquet.EnCours || pkg.progression === EProgressionPaquet.Complete;
  }

  get filteredFormationsGroupes(): GroupeFormationModel[] {
    return this.formationsGroupes.filter(fg => this.estGroupeFormationVisible(fg));
  }

  get hasPriorityContent(): boolean {
    const visibleParcours = this.parcoursFormations.filter(pf => this.estParcoursVisible(pf));
    const visibleGroupes = this.filteredFormationsGroupes.filter(fg => this.isParticipantTrainingGroup(fg) || this.isSupervisorTrainingGroup(fg));
    return visibleParcours.length > 0 || visibleGroupes.length > 0;
  }

  get allSections() {
    const sections = [];

    if (this.inProgressScormPackages.length > 0) {
      const filtered = this.inProgressScormPackages.filter(pkg => {
        if (this.filteredCategoryId === PRIORITE_CATEGORY_ID || !this.searchTerm.trim()) return true;
        const localized = this.getLocalizedContent(pkg.locales);
        return localized.titre && localized.titre.toLowerCase().includes(this.searchTerm.trim().toLowerCase());
      });
      if (filtered.length > 0) {
        sections.push({
          title: this.translate.instant('SCORMS_LIST.IN_PROGRESS_CATEGORY'),
          packages: filtered,
          icon: null,
          categorieIcon: null
        });
      }
    }

    for (const cat of this.displayedCategories) {
      const pkgs = this.getPackagesByCategory(cat.categorieId).filter(pkg => {
        if (this.filteredCategoryId === PRIORITE_CATEGORY_ID || !this.searchTerm.trim()) return true;
        const localized = this.getLocalizedContent(pkg.locales);
        return localized.titre && localized.titre.toLowerCase().includes(this.searchTerm.trim().toLowerCase());
      });
      if (pkgs.length > 0) {
        sections.push({
          title: this.getLocalizedCategoryName(cat.locales),
          packages: pkgs,
          icon: cat.categorieId === -1 ? 'play_arrow' : null,
          categorieIcon: cat.icone || null
        });
      }
    }

    return sections;
  }

  get shouldShowPriorityAndAllContent(): boolean {
    return this.filteredCategoryId === PRIORITE_CATEGORY_ID || this.filteredCategoryId === null;
  }

  get shouldShowNoVideosMessage(): boolean {
    const entite = this.utilisateurService.entiteUtilisateurSelectionnee;
    const filteredPackages = this.filtrerSelonServices(this.scormPackages);
    return (this.isDemoActive ||
      entite?.estServiceStudio === true ||
      entite?.estServiceStudioContenusPerso === true) &&
      filteredPackages.length === 0 &&
      !this.isLoading;
  }

  get currentEntiteServiceStudio(): boolean {
    return this.utilisateurService.entiteUtilisateurSelectionnee?.estServiceStudio ?? false;
  }

  get currentEntiteServiceStudioContenusPerso(): boolean {
    return this.utilisateurService.entiteUtilisateurSelectionnee?.estServiceStudioContenusPerso ?? false;
  }

  private createLoadingTracker(total: number, onAllLoaded: () => void): () => void {
    let loaded = 0;
    return () => {
      loaded++;
      if (loaded === total) {
        onAllLoaded();
      }
    };
  }

  private checkDemoCompletedContentCount(entiteId: number, utilisateurId: number, onLoaded: () => void): void {
    const dateDebut = new Date(this.utilisateurService.entiteUtilisateurSelectionnee.dateDebutDemo);
    const dateFin = new Date(this.utilisateurService.entiteUtilisateurSelectionnee.dateFinDemo);

    this.contenuSessionService.obtenirNombreSessionsCompleteesDemo(entiteId, utilisateurId, dateDebut, dateFin)
      .subscribe(count => {
        this.completedContentCount = count;
        this.demoContentCountLoaded = true;
        onLoaded();
      });
  }

  private estPackagePerso(pkg: ScormPackageModel): boolean {
    return pkg.entitePackagePerso != null;
  }

  private estPackageLegacy(pkg: ScormPackageModel): boolean {
    return pkg.entitePackagePerso?.estLegacy === true;
  }

  private isPackageVisibleByServices(pkg: ScormPackageModel): boolean {
    const entite = this.utilisateurService.entiteUtilisateurSelectionnee;
    const aServiceStudio = entite?.estServiceStudio ?? false;
    const aServicePerso = entite?.estServiceStudioContenusPerso ?? false;

    if (aServiceStudio && aServicePerso) {
      return true;
    }

    if (aServiceStudio) {
      return !this.estPackagePerso(pkg) || this.estPackageLegacy(pkg);
    }

    if (aServicePerso) {
      return this.estPackagePerso(pkg);
    }

    return false;
  }

  private filtrerSelonServices(packages: ScormPackageProgressionModel[]): ScormPackageProgressionModel[] {
    const entite = this.utilisateurService.entiteUtilisateurSelectionnee;
    if (!entite) return packages;

    return packages.filter(pkg => this.isPackageVisibleByServices(pkg));
  }

  private estParcoursVisible(parcours: ParcoursFormationUtilisateurModel): boolean {
    const entite = this.utilisateurService.entiteUtilisateurSelectionnee;
    if (!entite) return false;

    if (!parcours.packages || parcours.packages.length === 0) {
      return entite.estServiceStudio === true && entite.estServiceStudioContenusPerso === true;
    }

    return parcours.packages.some(pkg => this.isPackageVisibleByServices(pkg.scormPackage));
  }

  private estGroupeFormationVisible(groupe: GroupeFormationModel): boolean {
    const entite = this.utilisateurService.entiteUtilisateurSelectionnee;
    if (!entite || !groupe.package) return false;

    return this.isPackageVisibleByServices(groupe.package);
  }
}
